import { Box, Button, Modal, Typography, keyframes, SvgIcon } from "@mui/material";
import { colors } from "../../ui/colors";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

interface BenefitsModalProps {
    open: boolean;
    onClose: () => void;
    onPreview: () => void;
    featureName?: string;
    reason?: string;
}

export const BenefitsModal = ({
    open,
    onClose,
    onPreview,
    featureName = "this premium feature",
    reason
}: BenefitsModalProps) => {
    const isLimitReached = reason === "LIMIT_REACHED";

    const title = isLimitReached ? "Usage Limit Reached" : "Unlock More Power";
    const description = isLimitReached
        ? `You have reached your usage limit for ${featureName}. Upgrade your plan to increase your limits.`
        : `You need an upgraded plan to fully access ${featureName}. However, you can try it out and see how it works!`;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="benefits-modal-title"
            aria-describedby="benefits-modal-description"
            sx={{
                zIndex: 10000000000000,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 500 },
                    bgcolor: "background.paper",
                    borderRadius: 4,
                    boxShadow: 24,
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    outline: "none",
                    ...(!open && { pointerEvents: "none" }) // Important to avoid weird ghost elements
                }}
            >
                {/* Close Button */}
                <Box sx={{ position: "absolute", top: 16, right: 16 }}>
                    <Button
                        onClick={onClose}
                        sx={{ minWidth: "auto", p: 1, color: colors.gray[500], borderRadius: "50%" }}
                    >
                        <CloseIcon />
                    </Button>
                </Box>

                {/* Animated Icon */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        bgcolor: `${colors.orange.base}1A`, // 10% opacity
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        animation: `${bounce} 3s ease-in-out infinite`,
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 40, color: colors.orange.base }} />
                </Box>

                <Typography id="benefits-modal-title" variant="h5" fontWeight={800} textAlign="center" mb={2}>
                    {title}
                </Typography>

                <Typography id="benefits-modal-description" sx={{ color: colors.gray[600], textAlign: "center", mb: 3, lineHeight: 1.6 }}>
                    {description}
                </Typography>

                <Box sx={{ width: "100%", mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: colors.success, mr: 1.5, fontSize: 20 }} />
                        <Typography variant="body2" color={colors.gray[700]}>Explore all premium capabilities</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: colors.success, mr: 1.5, fontSize: 20 }} />
                        <Typography variant="body2" color={colors.gray[700]}>See how it fits your workflow</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <HelpOutlineIcon sx={{ color: colors.orange.base, mr: 1.5, fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={500} color={colors.orange.base}>Changes won't be saved in preview mode</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={onClose}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            borderColor: colors.gray[300],
                            color: colors.gray[700],
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                borderColor: colors.gray[400],
                                bgcolor: colors.gray[50]
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onPreview}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            backgroundColor: colors.orange.base,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: colors.orange[700],
                            }
                        }}
                    >
                        Preview Feature
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
